import { Fragment, useEffect, useState } from 'react';
// Redux
import { useAppDispatch } from 'app/hooks/useStore';
// Async
import { createInterimInvoice } from 'app/store/BillingInvoices/BillingInvoices.async';
// Actions
import { BillingUsageActions } from 'app/store/BillingUsage/BillingUsage.slice';
import { BillingInvoicesActions } from 'app/store/BillingInvoices/BillingInvoices.slice';
// Mui
import { Box, Grid, Stepper, Step, StepLabel } from '@mui/material';
// Components
import { Button } from 'app/components/Mui';
import Dialog from 'app/components/Dialog';
// 
import CaseStepContent from './CaseStepContent';
import DetailsStepContent from './DetailsStepContent';

type Props = {
  open: boolean;
  onClose: () => void;
  predefinedCase?:any
  initialStep?: number;
}

export default function RequestInterimInvoiceDialog({ open, onClose, predefinedCase, initialStep = 0 }: Props) {
  const steps = ['Select a case', 'Preview'];

  // Dispatch
  const dispatch = useAppDispatch();

  const [ activeStep, setActiveStep ] = useState(initialStep);
  const [ selectedCase, setSelectedCase ] = useState<any>(predefinedCase || null);
  const [ loading, setLoading ] = useState(false);
  const [ generateButtonDisabled, setGenerateButtonDisabled ] = useState(false);
  const [ submitted, setSubmitted ] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(BillingUsageActions.setInitialField('usages'));
      dispatch(BillingUsageActions.setInitialField('params'));
    }
  }, [dispatch])

  const handleGenerateClick = async () => {
    if ( !selectedCase ) return;
    setLoading(true);
    try {
      const billingInvoices = await dispatch(createInterimInvoice({ data: { caseId: selectedCase.id } })).unwrap();
      if ( billingInvoices.length ){
        dispatch(BillingInvoicesActions.addBillingInvoice(billingInvoices));
      }
      onClose();
    } catch(error){
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      title="Create Interim Invoice"
    >
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 3 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
          <Grid size={{ xs: 9 }}>
            {activeStep < steps.length && (
              <Box sx={{ minHeight: 480 }}>
                {activeStep === 0 && (
                  <Fragment>
                    <CaseStepContent submitted={submitted} selectedCase={selectedCase} onSetSelectedCase={setSelectedCase} />
                  </Fragment>
                )}
                {activeStep === 1 && (
                  <DetailsStepContent selectedCase={selectedCase} onSetGenerateButtonDisabled={setGenerateButtonDisabled} />
                )}
              </Box>
            )}
            <Box sx={{
              mt: 4,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2
            }}>
              <Button
                name="Prev step"
                disabled={activeStep === 0}
                onClick={() => setActiveStep((prev) => prev - 1)}
              >Prev</Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  name="Request invoice"
                  loading={loading}
                  disabled={generateButtonDisabled}
                  onClick={handleGenerateClick}
                  variant="contained"
                >Create</Button>
              ) : (
                <Button
                  name="Next step"
                  onClick={() => {
                    setSubmitted(true);
                    if ( !selectedCase ) return;
                    setActiveStep((prev) => prev + 1)
                  }}
                >Next</Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  )
}
